/**
 * Values fixed by the design documents, not by the business. Anything that is a
 * commercial or capacity number (cache size, price, slot count) is configuration
 * and lives in config.ts, where it fails loudly if unset.
 */

/** technical-design §11.3: a device with a dead broker degrades to a 60-second poll. */
export const BOOTSTRAP_POLL_INTERVAL_SECONDS = 60;

/** technical-design §6.6 / architecture-v2 §6.2: heartbeat every five minutes. */
export const BOOTSTRAP_HEARTBEAT_INTERVAL_SECONDS = 300;

/**
 * How often the player tells the native shell it is still looping.
 *
 * Not the API heartbeat. The shell's watchdog restarts the WebView when it has not heard from
 * the player for 90 seconds (`WatchdogPolicy.playerHeartbeatStaleMs`), so this has to stay
 * comfortably inside that: 30 seconds survives two missed calls. It is deliberately a constant
 * and not a manifest setting — a screen that cannot reach the network still has to keep its own
 * WebView from being restarted underneath it.
 */
export const ALIVE_REPORT_INTERVAL_SECONDS = 30;

/**
 * How often the pairing card checks the shell for a new code while it is held. The card is drawn
 * the moment a TV has no token, which is a moment before the shell has a code for it (a first
 * start, or a token just forgotten, D254), and a code that expires is replaced mid-hold. Waiting
 * out the 15-second hold left the card with no code, or a dead one, that long.
 */
export const PAIRING_CODE_WATCH_MS = 1_000;

/** technical-design §11.6: batched at 50. */
export const BOOTSTRAP_EVENT_BATCH_SIZE = 50;

/** technical-design §11.6: queue capped at 20,000 events, drop oldest, record a gap marker. */
export const EVENT_QUEUE_MAX_EVENTS = 20_000;

/** design-addendum §2: the blocklist publisher caps the list; the player enforces the cap too. */
export const BLOCKLIST_MAX_ENTRIES = 50;

/** technical-design §11.5: verify sha256, and re-download exactly once on a mismatch. */
export const ASSET_REDOWNLOAD_ATTEMPTS = 1;

/** design-addendum §11: flag clock drift over five minutes in the heartbeat. */
export const CLOCK_DRIFT_FLAG_MS = 5 * 60 * 1000;

/**
 * Clock sanity (optisigns-parity §3): a cheap stick with no NTP boots at the epoch
 * or at a factory date. Anything before this instant, or more than ten years after
 * it, is implausible and the player defers to server time from the first response.
 */
export const MIN_PLAUSIBLE_EPOCH_MS = Date.UTC(2026, 0, 1);
export const MAX_PLAUSIBLE_EPOCH_MS = MIN_PLAUSIBLE_EPOCH_MS + 10 * 365 * 24 * 60 * 60 * 1000;

/** Download backoff (technical-design §11.5: exponential with jitter). */
export const DOWNLOAD_BACKOFF_BASE_MS = 1_000;
export const DOWNLOAD_BACKOFF_MAX_MS = 60_000;
export const DOWNLOAD_MAX_ATTEMPTS = 5;

/**
 * A video that never fires `ended` must not stall the loop (§11.7). The watchdog
 * fires at the item's declared duration plus this grace.
 */
export const VIDEO_WATCHDOG_GRACE_MS = 5_000;

/** Ring buffer of structured log lines kept for the heartbeat's last_error and dwf-036. */
export const LOG_RING_SIZE = 200;
